import { authenticatedActorSchema, requireCapability, requireIndependentContractAcceptance, requireTenant, } from "../protocol/authority.js";
import { assuranceForContract, isCurrentAcceptedContractEnvelope, } from "../protocol/contract.js";
import { projectFirstUnknownCustomerView, } from "../protocol/first-unknown.js";
import { isCurrentUniversalWorkItem, } from "../protocol/work.js";
import { isCurrentVerdictReceipt, } from "../protocol/verdict.js";
import { universalConsoleOverviewSchema, universalConsoleOverviewSourceSchema, universalWorkDetailSchema, } from "./console-model.js";
export class UniversalConsoleAuthorityError extends Error {
    code = "worker-console-authority-denied";
    constructor(actorId, operation) {
        super(`Worker credential ${actorId} cannot perform authoritative console operation ${operation}.`);
        this.name = "UniversalConsoleAuthorityError";
    }
}
export class UniversalWorkDetailIntegrityError extends Error {
    code = "receipt-evidence-binding-unresolved";
    constructor(receiptId) {
        super(`Current Receipt ${receiptId} requires exactly one admitted Evidence Bundle.`);
        this.name = "UniversalWorkDetailIntegrityError";
    }
}
function requireConsoleAuthority(actor, tenantId, operation) {
    const parsed = authenticatedActorSchema.parse(actor);
    requireTenant(parsed, tenantId);
    if (parsed.roles.includes("worker")) {
        throw new UniversalConsoleAuthorityError(parsed.actorId, operation);
    }
    return parsed;
}
function workBindingKey(binding) {
    return `${binding.tenantId}\u0000${binding.workItemId}`;
}
function contractBindingKey(binding) {
    return [
        binding.tenantId,
        binding.workItemId,
        binding.contractId,
        binding.contractVersion,
        binding.contractDigest,
    ].join("\u0000");
}
function attemptBindingKey(binding) {
    return [contractBindingKey(binding), binding.attemptId, binding.inputSnapshotDigest].join("\u0000");
}
function evidenceBindingKey(binding) {
    return [
        attemptBindingKey(binding),
        binding.outputSnapshotDigest,
        binding.evidenceGraphDigest,
    ].join("\u0000");
}
const REQUIRED_ENVIRONMENT_CAPTURE_DIMENSIONS = [
    "contract_snapshot",
    "input_snapshot",
    "output_snapshot",
    "environment_scope",
    "execution_boundary",
    "capability_inventory",
    "dependency_lock",
    "feature_flags",
    "database_migrations",
    "provider_account",
    "configuration",
    "evaluator_versions",
    "replay_environment",
    "secret_custody",
];
export function projectUniversalCompletionOverview(overview) {
    const history = overview.current && overview.incomplete
        ? {
            work: [...overview.current.work, ...overview.incomplete.work],
            contracts: [...overview.current.contracts, ...overview.incomplete.contracts],
            attempts: [...overview.current.attempts, ...overview.incomplete.attempts],
            receipts: [...overview.current.receipts, ...overview.incomplete.receipts],
            reviews: [...overview.current.reviews, ...overview.incomplete.reviews],
            amendments: [...overview.current.amendments, ...overview.incomplete.amendments],
            learningProposals: [
                ...overview.current.learningProposals,
                ...overview.incomplete.learningProposals,
            ],
        }
        : overview;
    const currentWork = history.work.filter((work) => isCurrentUniversalWorkItem(work));
    const currentWorkKeys = new Set(currentWork.map((work) => workBindingKey({ tenantId: work.tenantId, workItemId: work.id })));
    const currentContracts = history.contracts.filter((contract) => isCurrentAcceptedContractEnvelope(contract) &&
        currentWorkKeys.has(workBindingKey({
            tenantId: contract.content.tenantId,
            workItemId: contract.content.workItemId,
        })));
    const currentContractKeys = new Set(currentContracts.map((contract) => contractBindingKey({
        tenantId: contract.content.tenantId,
        workItemId: contract.content.workItemId,
        contractId: contract.content.contractId,
        contractVersion: contract.content.version,
        contractDigest: contract.contractDigest,
    })));
    const currentContractByBinding = new Map(currentContracts.map((contract) => [
        contractBindingKey({
            tenantId: contract.content.tenantId,
            workItemId: contract.content.workItemId,
            contractId: contract.content.contractId,
            contractVersion: contract.content.version,
            contractDigest: contract.contractDigest,
        }),
        contract,
    ]));
    const currentContractByDigest = new Map(currentContracts.map((contract) => [
        `${contract.content.tenantId}\u0000${contract.contractDigest}`,
        contract,
    ]));
    const currentAttempts = history.attempts.filter((attempt) => currentWorkKeys.has(workBindingKey(attempt.binding)) &&
        currentContractKeys.has(contractBindingKey(attempt.binding)));
    const currentAttemptByBinding = new Map(currentAttempts.map((attempt) => [attemptBindingKey(attempt.binding), attempt]));
    const currentAttemptById = new Map(currentAttempts.map((attempt) => [
        `${attempt.binding.tenantId}\u0000${attempt.binding.attemptId}`,
        attempt,
    ]));
    const currentReceipts = history.receipts.filter((receipt) => {
        if (!isCurrentVerdictReceipt(receipt))
            return false;
        const binding = receipt.payload.binding;
        const attempt = currentAttemptByBinding.get(attemptBindingKey(binding));
        const contract = currentContractByBinding.get(contractBindingKey(binding));
        if (!attempt || !contract)
            return false;
        const derivedAssurance = assuranceForContract(contract, attempt.openedAt);
        return (derivedAssurance === "PROSPECTIVE" &&
            receipt.payload.assurance === derivedAssurance &&
            attempt.state === "SUBMITTED" &&
            attempt.outputSnapshotDigest === binding.outputSnapshotDigest);
    });
    const currentReceiptById = new Map(currentReceipts.map((receipt) => [
        `${receipt.payload.binding.tenantId}\u0000${receipt.payload.id}`,
        receipt,
    ]));
    const currentReviews = history.reviews.filter(({ request, resolution }) => {
        const receipt = currentReceiptById.get(`${request.tenantId}\u0000${request.receiptId}`);
        if (!receipt ||
            request.workItemId !== receipt.payload.binding.workItemId ||
            evidenceBindingKey(request.binding) !== evidenceBindingKey(receipt.payload.binding)) {
            return false;
        }
        return (resolution === null ||
            (resolution.reviewRequestId === request.id &&
                resolution.tenantId === request.tenantId &&
                resolution.workItemId === request.workItemId &&
                resolution.receiptId === request.receiptId));
    });
    const currentAmendments = history.amendments.filter(({ proposal, decision }) => {
        const baseContract = currentContractByDigest.get(`${proposal.tenantId}\u0000${proposal.baseContractDigest}`);
        if (!baseContract ||
            baseContract.content.workItemId !== proposal.workItemId ||
            proposal.proposedContent.tenantId !== proposal.tenantId ||
            proposal.proposedContent.workItemId !== proposal.workItemId ||
            proposal.proposedContent.supersedesContractDigest !== proposal.baseContractDigest) {
            return false;
        }
        if (decision === null)
            return true;
        if (decision.amendmentId !== proposal.id ||
            decision.tenantId !== proposal.tenantId ||
            decision.workItemId !== proposal.workItemId ||
            decision.baseContractDigest !== proposal.baseContractDigest ||
            decision.proposedContentDigest !== proposal.proposedContentDigest) {
            return false;
        }
        if (decision.decision === "REJECTED") {
            return decision.successorContractDigest === null && decision.replacementAttemptId === null;
        }
        if (decision.successorContractDigest !== proposal.proposedContentDigest)
            return false;
        const successorContract = currentContractByDigest.get(`${proposal.tenantId}\u0000${decision.successorContractDigest}`);
        if (!successorContract ||
            successorContract.content.workItemId !== proposal.workItemId ||
            successorContract.content.contractId !== proposal.proposedContent.contractId ||
            successorContract.content.version !== proposal.proposedContent.version ||
            successorContract.content.supersedesContractDigest !== proposal.baseContractDigest) {
            return false;
        }
        if (decision.replacementAttemptId === null)
            return true;
        const replacementAttempt = currentAttemptById.get(`${proposal.tenantId}\u0000${decision.replacementAttemptId}`);
        return Boolean(replacementAttempt &&
            replacementAttempt.binding.workItemId === proposal.workItemId &&
            replacementAttempt.binding.contractId === successorContract.content.contractId &&
            replacementAttempt.binding.contractVersion === successorContract.content.version &&
            replacementAttempt.binding.contractDigest === successorContract.contractDigest &&
            replacementAttempt.supersedesAttemptId !== null);
    });
    const currentWorkRecords = new Set(currentWork);
    const currentContractRecords = new Set(currentContracts);
    const currentAttemptRecords = new Set(currentAttempts);
    const currentReceiptRecords = new Set(currentReceipts);
    const currentReviewRecords = new Set(currentReviews);
    const currentAmendmentRecords = new Set(currentAmendments);
    return {
        current: {
            work: currentWork,
            contracts: currentContracts,
            attempts: currentAttempts,
            receipts: currentReceipts,
            reviews: currentReviews,
            amendments: currentAmendments,
            learningProposals: [],
        },
        incomplete: {
            work: history.work.filter((work) => !currentWorkRecords.has(work)),
            contracts: history.contracts.filter((contract) => !currentContractRecords.has(contract)),
            attempts: history.attempts.filter((attempt) => !currentAttemptRecords.has(attempt)),
            receipts: history.receipts.filter((receipt) => !currentReceiptRecords.has(receipt)),
            reviews: history.reviews.filter((review) => !currentReviewRecords.has(review)),
            amendments: history.amendments.filter((amendment) => !currentAmendmentRecords.has(amendment)),
            learningProposals: history.learningProposals,
        },
    };
}
/**
 * Flatten admitted evidence bundles into console artifact rows, each carrying
 * the receipts that cite its bundle.
 *
 * An evidence library has to answer "which artifact, from where, cited by
 * what"; the bundles hold that, but they also hold inline artifact bytes, so
 * the overview publishes this projection rather than the bundles themselves.
 * Citation is by evidence graph digest, the same binding a receipt signs.
 */
export function projectUniversalConsoleEvidenceArtifacts(input) {
    const citedByDigest = new Map();
    for (const receipt of input.receipts) {
        const digest = receipt.payload.binding.evidenceGraphDigest;
        const existing = citedByDigest.get(digest);
        if (existing)
            existing.push(receipt.payload.id);
        else
            citedByDigest.set(digest, [receipt.payload.id]);
    }
    const rows = [];
    for (const bundle of input.evidence) {
        const citedByReceiptIds = citedByDigest.get(bundle.binding.evidenceGraphDigest) ?? [];
        for (const admitted of bundle.artifacts) {
            const artifact = admitted.artifact;
            rows.push({
                artifactId: artifact.id,
                bundleDigest: bundle.submissionDigest,
                evidenceGraphDigest: bundle.binding.evidenceGraphDigest,
                workItemId: bundle.binding.workItemId,
                attemptId: bundle.binding.attemptId,
                kind: artifact.kind,
                mediaType: artifact.mediaType,
                contentDigest: artifact.contentDigest,
                sizeBytes: artifact.sizeBytes,
                sourceUri: artifact.provenance.sourceUri,
                collectionMethod: artifact.provenance.method,
                collectorActorId: artifact.provenance.collector.actorId,
                observedAt: artifact.provenance.observedAt,
                admittedAt: bundle.admittedAt,
                accessibility: artifact.accessibility,
                freshness: artifact.freshness,
                mutability: artifact.mutability,
                independence: admitted.independence.classification,
                bytesVerified: admitted.bytesVerified,
                citedByReceiptIds: [...citedByReceiptIds],
            });
        }
    }
    return rows;
}
/**
 * Build the public status projection once for every transport. Top-level record
 * arrays are current-only compatibility aliases. Historical union members stay
 * readable under `incomplete`, where a legacy PASS cannot be mistaken for live
 * completion authority.
 *
 * Adapter guarantees are effective claims, not static capability declarations:
 * they remain false until the runtime reports proven live mediated delivery.
 */
export function createUniversalConsoleOverview(input) {
    const source = universalConsoleOverviewSourceSchema.parse(input);
    const completion = projectUniversalCompletionOverview(source);
    const currentReceiptIds = new Set(completion.current.receipts.map((receipt) => receipt.payload.id));
    const environmentBindingProof = [
        ...completion.current.receipts,
        ...completion.incomplete.receipts,
    ].map((receipt) => ({
        receiptId: receipt.payload.id,
        workItemId: receipt.payload.binding.workItemId,
        attemptId: receipt.payload.binding.attemptId,
        contractDigest: receipt.payload.binding.contractDigest,
        inputSnapshotDigest: receipt.payload.binding.inputSnapshotDigest,
        outputSnapshotDigest: receipt.payload.binding.outputSnapshotDigest,
        signedEnvironmentBindingDigest: receipt.payload.replay.environmentDigest,
        replayEnvironmentDigest: receipt.payload.replay.environmentDigest,
        currentAuthority: currentReceiptIds.has(receipt.payload.id),
        liveCaptureState: currentReceiptIds.has(receipt.payload.id)
            ? "current_receipt_bound"
            : "history_only",
        requiredCaptureDimensions: [...REQUIRED_ENVIRONMENT_CAPTURE_DIMENSIONS],
        refusalSemantics: "missing-or-changed-live-capture-keeps-receipt-ineligible",
    }));
    const runtimeByKind = new Map(source.adapterRuntime.map((runtime) => [runtime.kind, runtime]));
    const declaredGuaranteeByKind = new Map(source.adapters.map((profile) => [profile.kind, profile.enforcementGuaranteed]));
    const adapterRuntime = source.adapterRuntime.map((runtime) => {
        const enforcementGuaranteed = Boolean(declaredGuaranteeByKind.get(runtime.kind) &&
            runtime.delivery === "LIVE_WEBHOOK" &&
            runtime.externalEffectsPossible &&
            runtime.liveDeliveryProven);
        return { ...runtime, enforcementGuaranteed };
    });
    const adapters = source.adapters.map((profile) => {
        const runtime = runtimeByKind.get(profile.kind);
        return {
            ...profile,
            enforcementGuaranteed: Boolean(profile.enforcementGuaranteed &&
                runtime?.delivery === "LIVE_WEBHOOK" &&
                runtime.externalEffectsPossible &&
                runtime.liveDeliveryProven),
        };
    });
    return universalConsoleOverviewSchema.parse({
        ...source,
        work: completion.current.work,
        contracts: completion.current.contracts,
        attempts: completion.current.attempts,
        receipts: completion.current.receipts,
        amendments: completion.current.amendments,
        reviews: completion.current.reviews,
        learningProposals: completion.current.learningProposals,
        adapters,
        adapterRuntime,
        environmentBindingProof,
        ...completion,
    });
}
export function createUniversalWorkDetail(input) {
    const completion = projectUniversalCompletionOverview({
        work: [input.work],
        contracts: input.contracts,
        attempts: input.attempts,
        receipts: input.receipts,
        reviews: input.reviews,
        amendments: input.amendments,
        learningProposals: input.learningProposals,
    });
    const { work: _currentWork, ...current } = completion.current;
    const { work: _incompleteWork, ...incomplete } = completion.incomplete;
    const currentReceiptIds = current.receipts.map((receipt) => receipt.payload.id);
    for (const receipt of current.receipts) {
        if (input.evidence.filter((evidence) => evidenceBindingKey(evidence.binding) === evidenceBindingKey(receipt.payload.binding)).length !== 1) {
            throw new UniversalWorkDetailIntegrityError(receipt.payload.id);
        }
    }
    return universalWorkDetailSchema.parse({
        protocolVersion: input.protocolVersion,
        generatedAt: input.generatedAt,
        work: input.work,
        current,
        incomplete,
        evidence: input.evidence,
        effects: input.effects,
        firstUnknownReviews: (input.firstUnknownReviews ?? []).map((review) => ({
            tenantId: review.tenantId,
            workItemId: input.work.id,
            projectId: review.projectId,
            processId: review.processId,
            openedAt: review.openedAt,
            reasonCodes: review.reasonCodes,
            historicalOccurrenceVerification: review.historicalOccurrenceVerification,
            authority: "HISTORICAL_ONLY",
            customerView: projectFirstUnknownCustomerView(review),
        })),
        recurrenceDriftReviews: input.recurrenceDriftReviews ?? [],
        actionAuthority: {
            completionAuthority: currentReceiptIds.length > 0 ? "CURRENT_PROSPECTIVE" : "HISTORICAL_ONLY",
            currentContractBindings: current.contracts.map((contract) => ({
                contractId: contract.content.contractId,
                contractVersion: contract.content.version,
                contractDigest: contract.contractDigest,
            })),
            currentAttemptIds: current.attempts.map((attempt) => attempt.binding.attemptId),
            currentReceiptIds,
        },
    });
}
export function createUniversalVerificationConsole(input) {
    const sessionActor = authenticatedActorSchema.parse(input.sessionActor);
    return {
        createWork(command) {
            const actor = requireConsoleAuthority(sessionActor, command.tenantId, "work:create");
            requireCapability(actor, "work:create");
            return input.service.createWork({
                tenantId: command.tenantId,
                projectId: command.projectId ?? null,
                workType: command.workType,
                title: command.title,
                description: command.description,
                completionIntent: command.completionIntent,
                consequence: command.consequence,
                source: command.source,
                requester: actor,
                transactionGuard: command.transactionGuard,
                idempotency: command.idempotency,
            });
        },
        acceptContract(command) {
            const actor = requireConsoleAuthority(sessionActor, command.content.tenantId, "contract:accept");
            requireIndependentContractAcceptance({
                accepter: actor,
                workerActorId: command.expectedWorkerActorId,
                tenantId: command.content.tenantId,
            });
            return input.service.acceptContract({
                content: command.content,
                compilation: command.compilation,
                compilerActor: input.compilerActor ?? actor,
                accepter: actor,
                authorityBasis: command.authorityBasis,
                deploymentMode: command.deploymentMode,
                expectedWorkerActorId: command.expectedWorkerActorId,
            });
        },
        proposeAmendment(command) {
            const actor = requireConsoleAuthority(sessionActor, command.baseContract.content.tenantId, "contract:amend:propose");
            requireCapability(actor, "contract:amend:propose");
            return input.service.proposeAmendment({ ...command, proposer: actor });
        },
        decideAmendment(command) {
            const actor = requireConsoleAuthority(sessionActor, command.amendment.tenantId, "contract:amend:decide");
            requireCapability(actor, "contract:amend:decide");
            return input.service.decideAmendment({
                ...command,
                actor,
                compilerActor: input.compilerActor,
            });
        },
        verify(command) {
            const actor = requireConsoleAuthority(sessionActor, command.evidence.binding.tenantId, "verification:issue");
            if (actor.actorId === command.evidence.submitter.actorId) {
                throw new UniversalConsoleAuthorityError(actor.actorId, "verification:issue");
            }
            return input.utility.verify(command);
        },
        decide(command) {
            const tenantId = command.receipt.payload.binding.tenantId;
            const actor = requireConsoleAuthority(sessionActor, tenantId, "enforcement:decide");
            return input.service.decide({
                receipt: command.receipt,
                policy: command.policy,
                actor,
                target: command.target,
                override: command.override ? { actor, ...command.override } : undefined,
            });
        },
        requestReview(command) {
            const actor = requireConsoleAuthority(sessionActor, command.receipt.payload.binding.tenantId, "review:request");
            requireCapability(actor, "review:request");
            return input.service.requestReview({ ...command, actor });
        },
        resolveReview(command) {
            const actor = requireConsoleAuthority(sessionActor, command.request.tenantId, "review:resolve");
            requireCapability(actor, "review:resolve");
            return input.service.resolveReview({ ...command, actor });
        },
        listWork(tenantId) {
            requireConsoleAuthority(sessionActor, tenantId, "work:list");
            return input.service.listWork(tenantId);
        },
        listReceipts(tenantId) {
            requireConsoleAuthority(sessionActor, tenantId, "receipt:list");
            return input.service.listReceipts(tenantId);
        },
    };
}
